import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';

const Test = () => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Timer state (15 minutes in seconds)
  const [timeLeft, setTimeLeft] = useState(15 * 60);
  const [testStarted, setTestStarted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Format time to MM:SS
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  useEffect(() => {
    const getQuestions = async () => {
      try {
        const res = await axios.get('https://iq-test-theta.vercel.app/api/test/questions');
        setQuestions(res.data);
        setLoading(false);
        setTestStarted(true);
      } catch (err) {
        setError('Error loading questions. Please try again.');
        setLoading(false);
      }
    };

    getQuestions();
  }, []);

  // Timer countdown
  useEffect(() => {
    if (!testStarted || timeLeft <= 0) return;

    const timerId = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerId);
          // Auto-submit when timer reaches zero
          submitTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [testStarted, timeLeft]);

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };

  const submitTest = async () => {
    if (submitting) return;

    setSubmitting(true);

    try {
      const formattedAnswers = Object.keys(answers).map(questionId => ({
        questionId: parseInt(questionId),
        selectedAnswer: answers[questionId]
      }));

      const timeTaken = 15 * 60 - timeLeft;

      const res = await axios.post('https://iq-test-theta.vercel.app/api/test/submit', {
        answers: formattedAnswers,
        timeTaken
      });

      // Update user state to show test completed
      auth.setTestCompleted();

      // Redirect to results page
      navigate('/results');
    } catch (err) {
      setError('Error submitting test. Please try again.');
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="container">Loading questions...</div>;
  }

  return (
    <div className="container">
      <div className="logout-container">
        <button className="logout-btn" onClick={() => auth.logout()}>Logout</button>
      </div>

      <div className="timer" style={{ position: 'fixed', top: '10px', right: '20px', padding: '10px', borderRadius: '5px', fontWeight: 'bold' }}>
        Time Left: {formatTime(timeLeft)}
      </div>

      <div className="test-container">
        <div className="test-instructions">
        <h1>IQ Test</h1>
        <div className="instruction-box">
        <p>
          To answer each question select the answer that completes each visual pattern. 
          If you do not know the answer, a guess is acceptable. 
          This test has 30 questions and a 15 minute time limit. 
          The questions are placed in a nearly random order. 
            For an accurate score, do not use any aids to complete this test and take it only once.
          </p>
          </div>
          {error && <div className="alert alert-danger">{error}</div>}
        </div>

        {questions.map((question, index) => (
          <div key={question.questionId} className="question-container">
            <h3>Question {index + 1}</h3>
            <img
              src={`https://iq-test-theta.vercel.app${question.imageUrl}`}
              alt={`Question ${question.questionId}`}
              className="question-image"
            />
            <div className="options-container">
              {question.totalOptions === 4
                ? ['A', 'B', 'C', 'D'].map((option) => (
                  <button
                    key={option}
                    className={`option-btn ${answers[question.questionId] === option ? 'selected' : ''}`}
                    onClick={() => handleAnswerSelect(question.questionId, option)}
                  >
                    {option}
                  </button>
                ))
                : ['A', 'B', 'C', 'D', 'E'].map((option) => (
                  <button
                    key={option}
                    className={`option-btn ${answers[question.questionId] === option ? 'selected' : ''}`}
                    onClick={() => handleAnswerSelect(question.questionId, option)}
                  >
                    {option}
                  </button>
                ))
              }
            </div>
          </div>
        ))}

        <button
          className="btn submit-btn"
          onClick={submitTest}
          disabled={submitting}
        >
          {submitting ? 'Submitting...' : 'Submit Test'}
        </button>
      </div>
    </div>
  );
};

export default Test;